import React, { useEffect, useRef, useState } from 'react';
import { PermissionsProvider } from '../../permissions/PermissionsContext';
import {
  FaChevronDown,
  FaComments,
  FaCompressAlt,
  FaExpandAlt,
} from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import TeamChat from './TeamChat';
import { DockSize, useChatDock } from '../../state/ChatDockContext';
import { useChatActivity } from '../../state/ChatActivityContext';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import '../../chat-dock.css';

// The default CSS width doubles as the width minimum; the height can
// shrink to half the 540px default. Maximums track the CSS
// max-width/max-height viewport margins.
const MIN_DOCK_WIDTH = 380;
const MIN_DOCK_HEIGHT = 270;
const DOCK_VIEWPORT_MARGIN_X = 32;
const DOCK_VIEWPORT_MARGIN_Y = 32;

const clampDockSize = (size: DockSize): DockSize => ({
  width: Math.round(
    Math.min(
      Math.max(size.width, MIN_DOCK_WIDTH),
      // Never let the max clamp below the min on tiny windows
      Math.max(MIN_DOCK_WIDTH, window.innerWidth - DOCK_VIEWPORT_MARGIN_X),
    ),
  ),
  height: Math.round(
    Math.min(
      Math.max(size.height, MIN_DOCK_HEIGHT),
      Math.max(MIN_DOCK_HEIGHT, window.innerHeight - DOCK_VIEWPORT_MARGIN_Y),
    ),
  ),
});

interface ResizeDrag {
  pointerId: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  /** Left edge resizes width, top edge height, the corner both axes */
  axis: 'left' | 'top' | 'corner';
}

/**
 * The always-available team chat, pinned to the bottom-right of every
 * console page from the moment the user logs in. Every team's chat stays
 * mounted (hidden when not in front) so sockets and transcripts survive
 * navigation; a header dropdown switches teams. The window minimizes to
 * a launcher bubble — it cannot be closed.
 */
const ChatDock: React.FC = () => {
  const {
    chats,
    activeTeamId,
    minimized,
    maximized,
    embeddedTeamId,
    dockSize,
    setActive,
    setMinimized,
    setMaximized,
    setDockSize,
  } = useChatDock();

  const { chatTotal, teamUnread } = useChatActivity();
  const [teamsOpen, setTeamsOpen] = useState(false);
  const teamsRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<ResizeDrag | null>(null);
  const location = useLocation();
  const isMobileLayout = useIsMobileLayout();

  // On phones the open dock fills the screen; maximized on desktop it sits
  // over a backdrop. Both are modal surfaces — the page behind must not
  // scroll, pan, or zoom. The floating desktop window keeps the page live.
  //
  // Modality, not measured size, is the trigger. A dock drag-resized to fill
  // the desktop viewport is still non-modal — no backdrop, page still
  // clickable — and the dock stays mounted on every console page, so keying
  // the lock off size would kill page scroll for good once someone dragged
  // it large. The lock and the resize handles are also mutually exclusive by
  // construction: maximizing hides the handles (see the render below), and
  // the ≤768px breakpoint sets them `display: none`, so the pinch guard
  // never competes with a drag.
  useBodyScrollLock(
    chats.length > 0 &&
      !embeddedTeamId &&
      !minimized &&
      (isMobileLayout || maximized),
  );

  // Dragging the top-left corner (both axes) or left edge (width) resizes
  // the small window. The dock is anchored bottom-right, so growing up/left
  // is just a larger width/height — pointer deltas are inverted. Pointer
  // capture keeps the drag alive when the cursor outruns the 6px handle.
  const beginResize =
    (axis: ResizeDrag['axis']) =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dock = dockRef.current;
      if (!dock) return;
      const rect = dock.getBoundingClientRect();
      resizeRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startWidth: rect.width,
        startHeight: rect.height,
        axis,
      };
      // Optional-chained: jsdom (unit tests) has no pointer capture
      event.currentTarget.setPointerCapture?.(event.pointerId);
      // Suppress text selection / focus steal for the duration of the drag
      event.preventDefault();
    };

  const onResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = resizeRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    setDockSize(
      clampDockSize({
        width:
          drag.axis === 'top'
            ? drag.startWidth
            : drag.startWidth + (drag.startX - event.clientX),
        height:
          drag.axis === 'left'
            ? drag.startHeight
            : drag.startHeight + (drag.startY - event.clientY),
      }),
    );
  };

  const endResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (resizeRef.current?.pointerId === event.pointerId) {
      resizeRef.current = null;
    }
  };

  // Opening the dock from the launcher matches the team context of the page
  // you're on: team-specific routes are `/console/team/<id>/…` (settings,
  // projects, board, wiki, databases, files, agents — all carry the team id
  // as that first segment). If the current page belongs to a team you have
  // in the dock, switch to it on open; otherwise just reopen the last one.
  const openDock = () => {
    const routeTeamId = location.pathname.match(/\/console\/team\/(\d+)/)?.[1];
    if (
      routeTeamId &&
      routeTeamId !== activeTeamId &&
      chats.some((chat) => chat.teamId === routeTeamId)
    ) {
      setActive(routeTeamId); // switches team AND clears minimized (opens)
    } else {
      setMinimized(false);
    }
  };

  useEffect(() => {
    if (!teamsOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!teamsRef.current?.contains(event.target as Node)) {
        setTeamsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [teamsOpen]);

  if (chats.length === 0) {
    return null;
  }

  // A team page hosts its own full-page chat: no floating dock (or
  // launcher) there. Like minimizing, this drops the dock's chat mounts;
  // transcripts restore from the server when the dock returns.
  if (embeddedTeamId) {
    return null;
  }

  const activeChat =
    chats.find((chat) => chat.teamId === activeTeamId) ?? chats[0];

  if (minimized) {
    return (
      <button
        className="chat-dock-launcher"
        onClick={openDock}
        data-testid="chat-dock-launcher"
        title="Open team chat">
        <FaComments size={20} />
        {chatTotal > 0 && (
          <span
            className="chat-dock-launcher-badge"
            data-testid="chat-dock-launcher-badge">
            {chatTotal > 99 ? '99+' : chatTotal}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {maximized && (
        <div
          className="chat-dock-backdrop"
          data-testid="chat-dock-backdrop"
          onClick={() => setMaximized(false)}
        />
      )}
      <div
        ref={dockRef}
        className={`chat-dock ${maximized ? 'chat-dock-maximized' : ''}`}
        style={
          !maximized && dockSize
            ? { width: dockSize.width, height: dockSize.height }
            : undefined
        }
        data-testid="chat-dock">
        {!maximized && (
          <>
            <div
              className="chat-dock-resize-left"
              data-testid="chat-dock-resize-left"
              onPointerDown={beginResize('left')}
              onPointerMove={onResizeMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
            />
            <div
              className="chat-dock-resize-top"
              data-testid="chat-dock-resize-top"
              onPointerDown={beginResize('top')}
              onPointerMove={onResizeMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
            />
            <div
              className="chat-dock-resize-corner"
              data-testid="chat-dock-resize-corner"
              onPointerDown={beginResize('corner')}
              onPointerMove={onResizeMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
            />
          </>
        )}
        <div className="chat-dock-header">
          {chats.length > 1 ? (
            <div className="chat-dock-team-picker" ref={teamsRef}>
              <button
                className="chat-dock-team-button"
                onClick={() => setTeamsOpen((open) => !open)}
                data-testid="chat-dock-team-menu"
                title="Switch team">
                <span className="chat-dock-team-name">
                  {activeChat.teamName}
                </span>
                <FaChevronDown size={9} />
              </button>
              {teamsOpen && (
                <div
                  className="chat-dock-team-options"
                  data-testid="chat-dock-team-options">
                  {chats.map((chat) => (
                    <button
                      key={chat.teamId}
                      className={`chat-dock-team-option ${
                        chat.teamId === activeChat.teamId
                          ? 'chat-dock-team-option-active'
                          : ''
                      }`}
                      onClick={() => {
                        setTeamsOpen(false);
                        setActive(chat.teamId);
                      }}
                      data-testid={`chat-dock-tab-${chat.teamId}`}>
                      {chat.teamName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="chat-dock-title">{activeChat.teamName}</span>
          )}
          <div className="chat-dock-actions">
            <button
              className="chat-dock-action chat-dock-maximize-action"
              onClick={() => setMaximized(!maximized)}
              data-testid="chat-dock-maximize"
              title={maximized ? 'Restore' : 'Maximize'}>
              {maximized ? (
                <FaCompressAlt size={11} />
              ) : (
                <FaExpandAlt size={11} />
              )}
            </button>
            <button
              className="chat-dock-action"
              onClick={() => setMinimized(true)}
              data-testid="chat-dock-minimize"
              title="Minimize">
              <FaChevronDown size={12} />
            </button>
          </div>
        </div>
        {chats.map((chat) => (
          <div
            key={chat.teamId}
            className="chat-dock-body"
            style={{
              display: chat.teamId === activeChat.teamId ? 'flex' : 'none',
            }}
            data-testid={`chat-dock-window-${chat.teamId}`}>
            {/* The dock is mounted globally (App.tsx), NOT under TeamPage, so
                nothing above it supplies permissions — and the approval
                controls inside a chat turn are guarded, which throws without a
                provider and takes the whole dock down with it. One provider per
                chat, keyed by that chat's team, because the dock can hold
                several teams at once and the answer differs per team. */}
            <PermissionsProvider key={chat.teamId} teamId={String(chat.teamId)}>
              <TeamChat teamId={chat.teamId} />
            </PermissionsProvider>
          </div>
        ))}
      </div>
    </>
  );
};

export default ChatDock;
